import React, { useEffect, useState } from 'react';

// Modal Component
const Modal = ({ message, taskAvailable, userBalance, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-md text-center">
        <h2 className="text-xl font-bold text-green-600">Task Status</h2>
        <p className="mt-4 text-lg">{message}</p>
        {userBalance !== 0 && userBalance !== undefined && (
          <p className="mt-4 text-lg text-green-600">
            Hooray! Your new balance is {userBalance} UXB.
          </p>
        )}
        {taskAvailable !== 0 && taskAvailable !== undefined && (
          <p className="text-gray-500 mt-2">{taskAvailable} more to go</p>
        )}
        {message !== "You have no tasks left to perform today." && (
          <button
            onClick={onClose}
            className="mt-4 px-6 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

const Tasks = () => {
  const [words, setWords] = useState([]);
  const [images, setImages] = useState([]);
  const [loadingWords, setLoadingWords] = useState(true);
  const [loadingImages, setLoadingImages] = useState(true);
  const [selectedWord, setSelectedWord] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [matchResult, setMatchResult] = useState(null); // 'correct' | 'incorrect' | null
  const [isMatched, setIsMatched] = useState({ word: false, image: false });
  const [modalOpen, setModalOpen] = useState(false);
  const [taskAvailable, setTaskAvailable] = useState(0);
  const [userBalance, setUserBalance] = useState(0);
  const [modalMessage, setModalMessage] = useState('');

  // Fetch words data and shuffle them
  useEffect(() => {
    const fetchWords = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tasks/words`);
        const data = await response.json();
        setWords(shuffleArray(data));
      } catch (error) {
        console.error('Error fetching words:', error);
      } finally {
        setLoadingWords(false);
      }
    };

    fetchWords();
  }, []);

  // Fetch images data and shuffle them
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tasks/images`);
        const data = await response.json();
        setImages(shuffleArray(data));
      } catch (error) {
        console.error('Error fetching images:', error);
      } finally {
        setLoadingImages(false);
      }
    };

    fetchImages();
  }, []);

  // Shuffle function
  const shuffleArray = (array) => {
    return array.sort(() => Math.random() - 0.5);
  };

  // Handle selection of word
  const handleSelectWord = (item) => {
    setSelectedWord(item);
    console.log(`Selected word: ${item?.id}`);
  };

  // Handle selection of image
  const handleSelectImage = (item) => {
    setSelectedImage(item);
    console.log(`Selected image: ${item?.id}`);
  };

  const handleMatch = async () => {
    if (selectedWord && selectedImage) {
      console.log(`Matching word ID ${selectedWord?.id} with image ID ${selectedImage?.id}`);
  
      if (selectedWord.id === selectedImage.id) {
        setMatchResult('correct');
        console.log('Correct match!');
  
        // Retrieve the access token from localStorage
        const accessToken = localStorage.getItem('accessToken');
  
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/perform-task`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              word_id: selectedWord.id,
              image_id: selectedImage.id,
            }),
          });
  
          if (response.ok) {
            const data = await response.json();
            console.log(data.message); // "Correct"
            console.log(data.task_available); // Task count
  
            // Set user balance and task availability
            setUserBalance(data.user_balance);
            setTaskAvailable(data.task_available);

            // Set the modal message based on the response
            setModalMessage(data.message);

            // Open the success modal
            setModalOpen(true);
  
            // Hide the matched word and image by setting their visibility state to true
            setIsMatched({ word: true, image: true });

            // Filter out the matched word and image
            setWords((prevWords) => prevWords.filter(word => word.id !== selectedWord.id));
            setImages((prevImages) => prevImages.filter(image => image.id !== selectedImage.id));

            // Reset selections and match result after a short delay
            setTimeout(() => {
              setSelectedWord(null);
              setSelectedImage(null);
              setMatchResult(null);
            }, 1000);
  
          } else {
            const errorData = await response.json();
            console.error('Error:', errorData.message);
            if (errorData.message === 'You have no tasks left to perform today.') {
              setModalMessage('Oops! Hey there, you can perform more tasks tomorrow.');
            } else if (errorData.message === 'Tasks cannot be performed on weekends.') {
              setModalMessage('Sorry, tasks cannot be performed on weekends. Please come to perform on Monday to Friday.');
            }
            setModalOpen(true);
          }
        } catch (error) {
          console.error('Network Error:', error);
          alert('There was an issue with the network. Please try again.');
        }
      } else {
        setMatchResult('incorrect');
        console.log('Incorrect match!');
        setTimeout(() => {
          setSelectedWord(null);
          setSelectedImage(null);
          setMatchResult(null);
        }, 1000);
      }
    }
  };

  // Real-time listener effect
  useEffect(() => {
    if (selectedWord && selectedImage) {
      handleMatch();
    }
  }, [selectedWord, selectedImage]);

  // Loading Skeleton
  const loadingSkeleton = (
    <div className="skeleton-item">
      <div className="bg-gray-300 h-[40px] w-[100px] rounded-md"></div>
    </div>
  );

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      {modalOpen && (
        <Modal
          message={modalMessage}
          taskAvailable={taskAvailable}
          userBalance={userBalance}
          onClose={handleCloseModal}
        />
      )}

      {/* <div className="flex">
        <div className="flex p-2 w-[50%] justify-center bg-slate-200 hover:bg-slate-300 text-[16px] cursor-pointer">All Tasks</div>
        <div className="flex p-2 w-[50%] justify-center bg-slate-100 hover:bg-slate-300 text-[16px] text-zinc-500 cursor-pointer">Incomplete</div>
      </div> */}

      <div className="flex flex-col gap-4 w-full h-full">
        <div className='flex flex-col max-h-[50%] overflow-y-auto scrollbar-hide w-full'>
          <div className="font-bold text-[28px] max-md:text-[22px]">Train AI</div>
          <div className="flex w-full  flex-wrap gap-2 items-center justify-evenly">
            {loadingWords
              ? Array.from({ length: 25 }).map((_, index) => (
                  <div key={index} className="bg-zinc-100 max-h-[48%] overflow-y-auto scrollbar-hide hover:bg-zinc-200 p-2 h-[40px] min-w-[45px] w-fit px-2 cursor-pointer">
                    {loadingSkeleton}
                  </div>
                ))
              : words.map((word) => (
                  <div
                    key={word?.id}
                    onClick={() => handleSelectWord(word)}
                    className={`bg-zinc-100 hover:bg-zinc-200 mb-4 p-2 h-[40px] min-w-[45px] w-fit px-2 cursor-pointer transition-opacity duration-300 ${
                      selectedWord && selectedWord.id === word?.id
                        ? 'border border-rose-500 shadow-lg shadow-cyan-500' // Apply blue border when selected
                        : ''
                    } ${matchResult === 'incorrect' && selectedWord && selectedWord.id === word.id ? 'border-red-500 shadow-lg shadow-red-500 animate-glitch' : ''} ${matchResult === 'correct' && selectedWord && selectedWord.id === word.id ? 'border-green-400 shadow-lg shadow-green-400' : ''} ${
                      isMatched.word === word?.id ? 'opacity-0' : ''
                    }`}
                  >
                    {word.word}
                  </div>
                ))}
          </div>
        </div>

        <div className="text-slate-400 max-md:text-[14px]">Select a word above and match by selecting with an image below</div>

        <div className="flex w-full max-h-[50%] overflow-y-auto scrollbar-hide flex-wrap gap-2 items-center justify-between">
          {loadingImages
            ? Array.from({ length: 25 }).map((_, index) => (
                <div key={index} className="flex flex-col flex-shrink-0 bg-white border hover:border-cyan-500 shadow-lg max-md:h-[100px] max-md:w-[100px] md:h-[120px] md:w-[120px] lg:h-[150px] lg:w-[150px] max-xl:h-[150px] max-xl:w-[150px] 2xl:h-[200px] 2xl:w-[200px] rounded-[12px] cursor-pointer items-center justify-center overflow-hidden">
                  {loadingSkeleton}
                </div>
              ))
            : images.map((image) => (
                <div
                  key={image?.id}
                  onClick={() => handleSelectImage(image)}
                  className={`flex flex-col flex-shrink-0 bg-white mb-4 border hover:border-cyan-500 shadow-lg max-md:h-[100px] max-md:w-[100px] md:h-[120px] md:w-[120px] lg:h-[150px] lg:w-[150px] max-xl:h-[150px] max-xl:w-[150px] 2xl:h-[200px] 2xl:w-[200px] rounded-[12px] cursor-pointer items-center justify-center overflow-hidden transition-opacity duration-300 ${
                    selectedImage && selectedImage.id === image?.id
                      ? 'border-rose-500 shadow-lg shadow-cyan-500' // Apply blue border when selected
                      : ''
                  } ${matchResult === 'incorrect' && selectedImage && selectedImage.id === image.id ? 'border-red-500 shadow-lg shadow-red-500 animate-glitch' : ''} ${matchResult === 'correct' && selectedImage && selectedImage.id === image.id ? 'border-green-400 shadow-lg shadow-green-400' : ''} ${
                    isMatched.image === image?.id ? 'opacity-0' : ''
                  }`}
                >
                  <img src={image?.imageUrl} alt="task" className="flex object-cover h-full w-full shrink-0" />
                </div>
              ))}
        </div>
      </div>
    </>
  );
};

export default Tasks;
